import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose, pure } from 'recompose';
import { bindActionCreators } from 'redux';
import styled, { css } from 'styled-components';
import { ifProp } from 'styled-tools';

import { APPLICATION_STEP_PATH } from 'constants/routes';
import ScrollToTopOnMount from 'hocs/ScrollToTopOnMount';
import { history } from 'store/configureStore';
import { Spinner, Icon } from 'components/shared';
import Wrapper from 'components/shared/Wrapper';
import { ScheduleTable, ScheduleHeader } from 'components/blocks/Schedule';
import { UserHeader } from 'components/blocks/User';
import { UserTemplate } from 'components/templates';
import * as actions from 'actions';
import { WithUser } from 'hocs';
import { userDefaultProps, userPropTypes } from 'propTypes';
import {
  getApplicationPolicy,
  getExpensesCollectionRefs,
  getSchedules,
  getSchedulesFromExternal,
  getSchedulesCollectionByGroup,
} from 'selectors';
import { getPath } from 'utils';
import { ApplicationHeader } from '../shared';
import { ScheduleTableProvider } from './context';

const propTypes = {
  ...userPropTypes,
  applications: PropTypes.shape({}),
  refs: PropTypes.shape({
    statesCollection: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })),
  }),
};

const defaultProps = {
  ...userDefaultProps,
  applications: {},
  refs: {
    statesCollection: [],
  },
};

const TABS = [
  { key: 'bridge', label: 'Bridge/Fix-Flip Properties' },
  { key: 'bridge-rent', label: 'Bridge/Fix-Rent Properties' },
  { key: 'new_construction', label: 'New Construction' },
  { key: 'rental', label: 'Rental Properties' },
  { key: 'primary', label: 'Primary Residence and Secondary Homes' },
];

const schedulesGroups = [
  { label: 'BRIDGE/FIX-FLIP', value: 'bridge' },
  { label: 'BRIDGE/FIX-RENT', value: 'bridge-rent' },
  { label: 'NEW CONSTRUCTION', value: 'new_construction' },
  { label: 'INVESTMENT PROPERTIES OWNED', value: 'rental' },
  { label: 'PRIMARY RESIDENCE & SECOND HOMES', value: 'primary' },
];

function UserApplicationSchedulePage(props) {
  const {
    schedule,
    user,
    refs,
    fetchApplicationFourthStep,
    fetchApplicationThirdStep,
    fetchSchedules,
    fetchSchedulesFromExternal,
    downloadTemplate,
    importApplicationSchedule,
    match,
    bridgeCollection,
    bridgeRentCollection,
    newConstructionCollection,
    rentalCollection,
    primaryCollection,
    importId,
  } = props;

  const [legalName, setLegalName] = useState(null);
  const [stateCodeName, setStateCodeName] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('bridge');
  const [borrowers, setBorrowers] = useState({});

  const { token } = match.params;

  useEffect(() => {
    Promise.all([
      fetchApplicationThirdStep(token),
      fetchApplicationFourthStep(token),
      fetchSchedules(token),
    ])
      .then(([thirdStepInfo, fourthStepInfo, schedulesInfo]) => {
        setStateCodeName(thirdStepInfo.application.borrower.stateCode);
        setLegalName(fourthStepInfo.application.entity.legalName);
        setBorrowers(schedulesInfo.collection.reSchedulesFetchParams.borrower || []);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, [fetchApplicationFourthStep, fetchApplicationThirdStep, fetchSchedules, token]);

  const stateName = refs.statesCollection
    ? refs.statesCollection.find((state) => state.value === stateCodeName)
    : null;

  const getTabCount = (tabKey) => {
    if (isLoading) return 0;

    switch (tabKey) {
      case 'bridge':
        return bridgeCollection ? bridgeCollection.length : 0;
      case 'new_construction':
        return newConstructionCollection ? newConstructionCollection.length : 0;
      case 'bridge-rent':
        return bridgeRentCollection ? bridgeRentCollection.length : 0;
      case 'rental':
        return rentalCollection ? rentalCollection.length : 0;
      case 'primary':
        return primaryCollection ? primaryCollection.length : 0;
      default:
        return 0;
    }
  };

  const onLlcClicked = () => {
    const newPath = getPath(APPLICATION_STEP_PATH, { token, step: 4 });
    history.push(newPath);
  };

  const onStateClicked = () => {
    const newPath = getPath(APPLICATION_STEP_PATH, { token, step: 3 });
    history.push(newPath);
  };

  const onDownloadTemplateClicked = async () => {
    try {
      await downloadTemplate(token);
    } catch (error) {
      // Error handled by redux action
    }
  };

  if (!user || isLoading) {
    return <Spinner />;
  }

  return (
    <ScheduleTableProvider
      user={user ? user.contact : null}
      stateName={stateName}
      legalName={legalName}
      token={token}
      fetchSchedulesFromExternal={fetchSchedulesFromExternal}
      importId={importId}
      borrowers={borrowers}
    >
      <UserTemplate header={<UserHeader />}>
        <ApplicationHeader />
        <StyledWrapper>
          <TitleWrapper>
            <ScheduleTitle>Real Estate Information
              <ScheduleSubtitle>
                Search by LLC or Upload a file below
              </ScheduleSubtitle>
            </ScheduleTitle>
            <HelperTextWrapper>
              <IconDiv>
                <Icon icon="lightbulb" width={2} />
              </IconDiv>
              <Title>
                Tell us about any properties you own more than 20%
              </Title>
            </HelperTextWrapper>
          </TitleWrapper>
          <ScheduleHeader
            onLlcClicked={onLlcClicked}
            onStateClicked={onStateClicked}
            onDownloadTemplateClicked={onDownloadTemplateClicked}
            importApplicationSchedule={importApplicationSchedule}
          />
          <RescheduleWrapper>
            <TabsWrapper>
              {TABS.map((tab) => (
                <TabButton
                  key={tab.key}
                  active={activeTab === tab.key}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label} ({getTabCount(tab.key)})
                </TabButton>
              ))}
            </TabsWrapper>
            {TABS.map((tab) => {
              if (activeTab === tab.key) {
                return (
                  <ScheduleTable
                    key={tab.key}
                    title={schedulesGroups.find((g) => g.value === tab.key).label}
                    group={tab.key}
                    target={schedule.target}
                    targetType={schedule.targetType}
                    targetId={schedule.targetId}
                  />
                );
              }

              return null;
            })}
          </RescheduleWrapper>
        </StyledWrapper>
        <div id="modalPortal" />
      </UserTemplate>
    </ScheduleTableProvider>
  );
}

const ScheduleTitle = styled.h1`
  font-size: var(--font-size-l);
  font-weight: bold;
  margin: 1rem 0;
  color: var(--color-primary);
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 768px) {
    font-size: var(--font-size-m);
  }
`;

const ScheduleSubtitle = styled.p`
  font-size: var(--font-size-s);
  color: var(--color-primary);
`;

export const RescheduleWrapper = styled.div`
  width: 100%;
`;

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  font-family: 'Manrope', sans-serif;
  color: var(--color-primary);
  text-align: center;
  
  @media (max-width: 1024px) {
    text-align: left;
    max-width: 400px;
  }
`;
const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  
  @media (max-width: 1024px) {
    margin-top: 1rem;
    align-items: flex-start;
    gap: 1rem;
  }
    @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const HelperTextWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  margin-top: 1rem;
  margin-bottom: 1rem;
  padding: 0.5rem; 
  color: var(--color-primary);
  
  @media (max-width: 1024px) {
    align-items: flex-start;
    margin-top: 0;
  }
  @media (max-width: 768px) {
    padding: 0;
  }
`;

const StyledWrapper = styled(Wrapper)`
  width: 100%;
  max-width: 90rem;
  position: relative;
  background-color: oklch(98.4% .003 247.858);
  font-family: 'Montserrat', sans-serif;
  line-height: 1.5em;
`;

const TabsWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  align-items: center;
  justify-content: center;
  height: 2.5rem;
  background: #f1f5f9;
  padding: 0.25rem;
  color: var(--color-secondary-text);
  margin-bottom: 1rem;
  outline: none;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    height: auto;
    row-gap: 0.5rem;
    padding: 0.5rem 0.25rem;
  }
`;

const IconDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--color-icon-background);
  border-radius: 100%;
  margin-right: 1rem;
  color: var(--color-icon);
`;

const TabButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  padding: 0.375rem 0.75rem; 
  font-size: 0.875rem; 
  font-weight: 500; 
  background: transparent;
  color: var(--color-secondary-text);;
  border: none;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, box-shadow 0.2s;
  outline: none;
  position: relative;

  &:hover {
    background: var(--color-secondary);
    color: #fff;
  }

  ${ifProp(
  'active',
  css`
      background: #052959;
      color: #fff; 
      box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.08);
    `,
)}
`;
const ScheduleHeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

UserApplicationSchedulePage.propTypes = propTypes;
UserApplicationSchedulePage.defaultProps = defaultProps;

function mapStateToProps(state, props) {
  const availableToImportReSchedule = getApplicationPolicy(state, 'availableToImportReSchedule');

  return {
    schedule: getSchedules(state, props),
    importId: state.schedules.importId,
    externalSchedules: getSchedulesFromExternal(state, props),
    availableToImportReSchedule,
    refs: getExpensesCollectionRefs(state),
    bridgeCollection: getSchedulesCollectionByGroup(state, 'bridge'),
    newConstructionCollection: getSchedulesCollectionByGroup(state, 'new_construction'),
    bridgeRentCollection: getSchedulesCollectionByGroup(state, 'bridge-rent'),
    rentalCollection: getSchedulesCollectionByGroup(state, 'rental'),
    primaryCollection: getSchedulesCollectionByGroup(state, 'primary'),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    fetchSchedulesFromExternal: bindActionCreators(actions.fetchSchedulesFromExternal, dispatch),
    fetchApplicationFourthStep: bindActionCreators(actions.fetchApplicationFourthStep, dispatch),
    fetchApplicationThirdStep: bindActionCreators(actions.fetchApplicationThirdStep, dispatch),
    fetchSchedules: bindActionCreators(actions.fetchSchedulesDirectly, dispatch),
    importApplicationSchedule: bindActionCreators(
      actions.uploadApplicationScheduleRequest,
      dispatch,
    ),
    downloadTemplate: bindActionCreators(actions.downloadApplicationTemplate, dispatch),
  };
}

const enhance = compose(
  ScrollToTopOnMount,
  WithUser,
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  pure,
);

export default enhance(UserApplicationSchedulePage);
